import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../api";
import { agoIso, fmtTime } from "../format";
import { navigate, useRoute } from "../route";
import type { LatencyPage, LatencyStats, TaskEnv, TurnMetric } from "../types";
import { DeliveryLatency } from "./DeliveryLatency";
import { secs, Tile } from "./latencyParts";

/**
 * Reply latency, one row per inbound iMessage turn.
 *
 * Answers "how long are people waiting for atula to answer, and why?" Before
 * this, a slow reply was only discovered when someone noticed it on their own
 * phone, and the cause had to be rebuilt by hand from log lines (2026-10-14: a
 * 100s reply to "hi" — 87s of it one Gemini call). Every row is a
 * `turn_metrics` document; see the engine's `storage/turn_metrics.py`.
 *
 * What "reply time" means here: worker start → reply handed to Sendblue. It is
 * OUR side. Sendblue's inbound leg and the queue wait are shown separately in
 * the breakdown, so a slow carrier is never blamed on the model or vice versa.
 *
 * "New users" = first contact (the phone's onboarding record) within 7 days of
 * the turn — relative to the turn, so a user's first week stays visible as
 * "new" when you look back a month later.
 *
 * "First reply after idle" = the turn after the conversation sat quiet long
 * enough (30 min) for its cache to expire, so it was reloaded from Firestore
 * before the model ran. The engine calls this a cold turn; the UI says what it
 * means instead. The two filters are independent and both live in the URL.
 *
 * Read-only.
 */

const ENVS: { key: TaskEnv; label: string }[] = [
  { key: "production", label: "Production" },
  { key: "development", label: "Dev" },
];

/** The engine accepts exactly these windows (hours). */
const RANGES: { hours: number; label: string }[] = [
  { hours: 24, label: "24h" },
  { hours: 24 * 7, label: "7d" },
  { hours: 24 * 30, label: "30d" },
];

/** Plain-words labels for the engine's cold/warm split. */
const IDLE_LABEL = "First reply after idle (30+ min quiet)";
const FOLLOW_LABEL = "Subsequent replies";

/** 31234 → "31k"; small counts stay exact. */
function tok(v: number | undefined): string {
  if (!v) return "0";
  return v >= 10000 ? `${Math.round(v / 1000)}k` : v >= 1000 ? `${(v / 1000).toFixed(1)}k` : `${v}`;
}

function modelS(r: TurnMetric): number | null {
  return r.model_ms === null || r.model_ms === undefined ? null : r.model_ms / 1000;
}

/** Whatever the turn spent outside the debounce and the model call: session
 *  load, consent gate, tool execution, sending. Clamped at 0 — the stages are
 *  timed on different clocks and can overlap by a few ms. */
function otherS(r: TurnMetric): number {
  return Math.max(0, r.total_s - (r.debounce_s ?? 0) - (modelS(r) ?? 0));
}

/**
 * First reply after idle vs the replies after it, side by side, so the gap is
 * visible without toggling the Turns filter. Fed from an unfiltered-by-turns
 * summary (see `compare` in Latency), so it stays meaningful when the table is
 * narrowed to first replies only.
 */
function Comparison({
  idle,
  follow,
}: {
  idle: { total_s: LatencyStats; model_s: LatencyStats };
  follow: { total_s: LatencyStats; model_s: LatencyStats };
}) {
  const col = (label: string, part: typeof idle) => (
    <div className="compare-col">
      <div className="compare-title">{label}</div>
      <div className="compare-grid">
        <Tile label="Reply p50" value={secs(part.total_s.p50)} />
        <Tile label="Reply p95" value={secs(part.total_s.p95)} />
        <Tile label="Model p50" value={secs(part.model_s.p50)} />
        <Tile label="Turns" value={`${part.total_s.count}`} />
      </div>
    </div>
  );
  return (
    <div className="compare">
      {col(IDLE_LABEL, idle)}
      {col(FOLLOW_LABEL, follow)}
    </div>
  );
}

/**
 * Reply time per turn over the window, cold and warm in different colours,
 * with the slow threshold drawn in. Inline SVG: one chart does not justify a
 * charting dependency, and a scatter keeps every outlier visible — an average
 * line would have hidden the one 100s turn this view exists to catch.
 */
function Scatter({
  rows,
  since,
  threshold,
}: {
  rows: TurnMetric[];
  since: string;
  threshold: number;
}) {
  const W = 900;
  const H = 220;
  const pad = { l: 44, r: 12, t: 12, b: 26 };
  const t0 = new Date(since).getTime();
  const t1 = Date.now();
  const maxY = Math.max(threshold * 1.4, ...rows.map((r) => r.total_s)) * 1.05;
  const x = (iso: string) =>
    pad.l + ((new Date(iso).getTime() - t0) / Math.max(1, t1 - t0)) * (W - pad.l - pad.r);
  const y = (v: number) => H - pad.b - (v / maxY) * (H - pad.t - pad.b);
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => Math.round(f * maxY));
  const fmtAxis = (ms: number) =>
    new Date(ms).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric" });

  return (
    <figure className="chart">
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Reply time per turn">
        {ticks.map((v) => (
          <g key={v}>
            <line x1={pad.l} x2={W - pad.r} y1={y(v)} y2={y(v)} className="chart-grid" />
            <text x={pad.l - 6} y={y(v) + 4} className="chart-axis" textAnchor="end">
              {v}s
            </text>
          </g>
        ))}
        <line
          x1={pad.l}
          x2={W - pad.r}
          y1={y(threshold)}
          y2={y(threshold)}
          className="chart-threshold"
        />
        <text x={W - pad.r} y={y(threshold) - 4} className="chart-axis chart-threshold-label" textAnchor="end">
          slow &gt; {threshold}s
        </text>
        <text x={pad.l} y={H - 6} className="chart-axis">
          {fmtAxis(t0)}
        </text>
        <text x={W - pad.r} y={H - 6} className="chart-axis" textAnchor="end">
          now
        </text>
        {rows.map((r, i) => (
          <circle
            key={`${r.created_at}-${i}`}
            cx={x(r.created_at)}
            cy={y(r.total_s)}
            r={r.total_s > threshold ? 5 : 3.5}
            className={r.cold ? "dot dot-cold" : "dot dot-warm"}
          >
            <title>
              {`${fmtTime(r.created_at)} — ${secs(r.total_s)} (${r.cold ? "first reply after idle" : "subsequent reply"}` +
                `${modelS(r) !== null ? `, model ${secs(modelS(r))}` : ""})`}
            </title>
          </circle>
        ))}
      </svg>
      <figcaption className="muted small">
        <span className="legend legend-warm" /> subsequent reply{" "}
        <span className="legend legend-cold" /> first reply after idle (30+ min quiet)
      </figcaption>
    </figure>
  );
}

export function Latency() {
  const route = useRoute();
  const env = route.env;

  // Both filters live in the URL (like env), so "new users' first reply after
  // idle" is a link you can send. Users defaults to New: the question this view
  // was built for is how a NEW user's messages feel.
  const newUsers = route.users === "new";
  const idleOnly = route.turns === "idle";
  const go = (patch: Partial<Pick<typeof route, "env" | "users" | "turns">>) =>
    navigate({ tab: "latency", env, users: route.users, turns: route.turns, ...patch });

  const [hours, setHours] = useState(24 * 7);
  const [slowOnly, setSlowOnly] = useState(false);
  const [page, setPage] = useState<LatencyPage | null>(null);
  // The comparison panel needs both halves even when the table shows only first
  // replies, so with the Turns filter on it comes from a second, turns-
  // unfiltered read (limit=1: only its summary is used).
  const [compare, setCompare] = useState<LatencyPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const main = api.latency(env, { hours, newUsers, cold: idleOnly ? true : null });
      const both = idleOnly ? api.latency(env, { hours, newUsers, limit: 1 }) : null;
      const [m, b] = await Promise.all([main, both]);
      setPage(m);
      setCompare(b ?? m);
      setError("");
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [env, hours, newUsers, idleOnly]);

  useEffect(() => {
    void load();
  }, [load]);

  const s = page?.summary;
  const threshold = s?.slow_threshold_s ?? 45;
  const rows = useMemo(() => page?.items ?? [], [page]);
  const visible = useMemo(
    () => (slowOnly ? rows.filter((r) => r.total_s > threshold) : rows),
    [rows, slowOnly, threshold],
  );

  return (
    <section>
      <header className="section-head">
        <h2>Reply latency</h2>
        <button className="ghost" onClick={() => void load()} disabled={loading}>
          {loading ? "Loading…" : "Refresh"}
        </button>
      </header>

      <p className="muted small">
        How long each inbound iMessage took to answer, and where the time went.
        Reply time is our side (worker start → reply handed to Sendblue); every
        turn also pays the ~8s wait for more bubbles.
      </p>

      <div className="tabs">
        {ENVS.map((e) => (
          <button
            key={e.key}
            className={env === e.key ? "tab active" : "tab"}
            onClick={() => go({ env: e.key })}
          >
            {e.label}
          </button>
        ))}
      </div>

      <div className="tabs">
        {RANGES.map((r) => (
          <button
            key={r.hours}
            className={hours === r.hours ? "tab active" : "tab"}
            onClick={() => setHours(r.hours)}
          >
            {r.label}
          </button>
        ))}
      </div>

      <div className="filters">
        <div className="tabs">
          <span className="filter-label">Users</span>
          <button
            className={newUsers ? "tab active" : "tab"}
            onClick={() => go({ users: "new" })}
            title={`Users whose first contact was within ${page?.new_user_days ?? 7} days of the turn`}
          >
            New
          </button>
          <button className={!newUsers ? "tab active" : "tab"} onClick={() => go({ users: "all" })}>
            Everyone
          </button>
        </div>
        <div className="tabs">
          <span className="filter-label">Turns</span>
          <button className={!idleOnly ? "tab active" : "tab"} onClick={() => go({ turns: "all" })}>
            All
          </button>
          <button
            className={idleOnly ? "tab active" : "tab"}
            onClick={() => go({ turns: "idle" })}
            title="The first reply after the conversation sat quiet 30+ minutes, so it had to be reloaded before the model ran"
          >
            First reply after idle
          </button>
        </div>
      </div>

      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}

      {s && (
        <div className="tiles">
          <Tile
            label="Turns"
            value={`${s.count}`}
            sub={`${newUsers ? "new users" : "all users"} · ${idleOnly ? "first reply after idle" : "all turns"}`}
          />
          <Tile label="Reply p50" value={secs(s.total_s.p50)} />
          <Tile label="Reply p90" value={secs(s.total_s.p90)} />
          <Tile label="Reply p95" value={secs(s.total_s.p95)} />
          <Tile label="Slowest" value={secs(s.total_s.max)} tone={(s.total_s.max ?? 0) > threshold ? "bad" : undefined} />
          <Tile
            label={`Slow (> ${threshold}s)`}
            value={`${s.slow_count}`}
            tone={s.slow_count > 0 ? "bad" : undefined}
            sub={s.count ? `${Math.round((s.slow_count / s.count) * 100)}% of turns` : undefined}
          />
          <Tile label="Model call p50" value={secs(s.model_s.p50)} sub={`p95 ${secs(s.model_s.p95)} · max ${secs(s.model_s.max)}`} />
        </div>
      )}

      {compare && compare.summary.count > 0 && (
        <Comparison idle={compare.summary.cold} follow={compare.summary.warm} />
      )}

      {page && rows.length > 0 && <Scatter rows={rows} since={page.since} threshold={threshold} />}

      {!loading && page && rows.length === 0 && (
        <p className="muted empty">
          No {idleOnly ? "first-reply-after-idle " : ""}turns {newUsers ? "from new users " : ""}in this window
          {env === "development" ? " on dev" : ""}. Metrics are recorded from the
          engine release that added them onward.
        </p>
      )}

      {rows.length > 0 && (
        <>
          <div className="tabs">
            <button className={!slowOnly ? "tab active" : "tab"} onClick={() => setSlowOnly(false)}>
              All turns<span className="count">{rows.length}</span>
            </button>
            <button className={slowOnly ? "tab active" : "tab"} onClick={() => setSlowOnly(true)}>
              Slow only
              <span className="count">{rows.filter((r) => r.total_s > threshold).length}</span>
            </button>
          </div>
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Time ▾</th>
                  <th>User</th>
                  <th>Outcome</th>
                  <th className="right">Reply</th>
                  <th>Breakdown</th>
                  <th>Tokens (in · think · out)</th>
                  <th>Turn</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((r, i) => {
                  const slow = r.total_s > threshold;
                  const m = modelS(r);
                  return (
                    <tr key={`${r.created_at}-${i}`} className={slow ? "row-warn" : undefined}>
                      <td className="nowrap" title={fmtTime(r.created_at)}>
                        <span className="muted">{agoIso(r.created_at)}</span>
                      </td>
                      <td>
                        <button
                          className="linklike small"
                          onClick={() => navigate({ tab: "users", q: r.user_id })}
                          title="Open this user"
                        >
                          <code>{r.user_id}</code>
                        </button>
                        {r.is_new_user && <span className="badge"> new</span>}
                      </td>
                      <td className="nowrap small muted">{r.outcome}</td>
                      <td className="right nowrap">
                        <strong className={slow ? "slow" : undefined}>{secs(r.total_s)}</strong>
                      </td>
                      <td className="small nowrap">
                        {m !== null ? (
                          <>
                            <span title="Gemini generate call">model {secs(m)}</span>
                            <span className="muted"> · wait {secs(r.debounce_s ?? 0)}</span>
                            <span className="muted"> · other {secs(otherS(r))}</span>
                          </>
                        ) : (
                          <span className="muted">no model call</span>
                        )}
                        {(r.queue_wait_s ?? 0) > 2 || (r.inbound_lag_s ?? 0) > 2 ? (
                          <div className="muted">
                            + queue {secs(r.queue_wait_s)} · Sendblue {secs(r.inbound_lag_s)}
                          </div>
                        ) : null}
                      </td>
                      <td className="small nowrap muted">
                        {r.tokens && r.tokens.total
                          ? `${tok(r.tokens.prompt)} · ${tok(r.tokens.thoughts)} · ${tok(r.tokens.output)}`
                          : "—"}
                      </td>
                      <td className="nowrap small">
                        {r.cold === null ? (
                          <span className="muted">—</span>
                        ) : (
                          <span
                            className={r.cold ? "badge warn" : "badge"}
                            title={r.cold ? IDLE_LABEL : FOLLOW_LABEL}
                          >
                            {r.cold ? "after idle" : "follow-up"}
                          </span>
                        )}
                        {r.turn_number ? <span className="muted"> #{r.turn_number}</span> : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {page && (
        <p className="muted small">
          {visible.length} shown · read from <code>{page.collection}</code>
          {page.complete
            ? ` (${page.scanned} turns in the window)`
            : ` — the newest ${page.scanned} turns only; older ones in the window were not read`}
        </p>
      )}

      <DeliveryLatency env={env} hours={hours} />
    </section>
  );
}
